import { useZero } from "@rocicorp/zero/react";
import { useEffect, useMemo, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { recapThroughEntryId } from "../../shared/recap";
import type { SlashCommand } from "../components/Chat/SlashMenu";
import { useFeatureFlag } from "./useFeatureFlag";

type CommandEntry = {
  id: string;
  entryType: string;
  recap?: { throughEntryId: string } | null;
};

export function useRecapCommand({
  chatId,
  entries,
}: {
  chatId: string | null;
  entries: readonly CommandEntry[];
}): { command: SlashCommand | null; pending: boolean } {
  const z = useZero();
  const enabled = useFeatureFlag("chat-recaps");
  const recapCount = useMemo(
    () => entries.filter((e) => e.entryType === "Recap").length,
    [entries]
  );
  // The recap count at request time; the recap arriving via sync clears it.
  const [countAtRequest, setCountAtRequest] = useState<number | null>(null);
  useEffect(() => {
    if (countAtRequest !== null && recapCount > countAtRequest) {
      setCountAtRequest(null);
    }
  }, [recapCount, countAtRequest]);
  const available = useMemo(
    () => recapThroughEntryId(entries, true) !== null,
    [entries]
  );
  const command = useMemo<SlashCommand | null>(
    () =>
      enabled && chatId && available
        ? {
            id: "recap",
            icon: "bookmark",
            label: "Recap this conversation",
            run: () => {
              setCountAtRequest(recapCount);
              z.mutate(
                sharedMutators.generateChatRecap({ chatId, onDemand: true })
              );
            },
          }
        : null,
    [enabled, chatId, available, recapCount, z]
  );
  return { command, pending: countAtRequest !== null };
}
